// src/layouts/brand/BrandLayout.tsx
import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Menu } from "react-feather";
import Sidebar from "../../components/Sidebar/Sidebar";
import { useBrandIdentity } from "../../hooks/useBrandIdentity";
import { useFavicon } from "@/lib/favicon";
import { ModalProvider } from "../../contexts/modal/provider";
import { BrandProvider } from "../../contexts/BrandContext/provider";
import { DriverProvider } from "../../contexts/driver";
import "driver.js/dist/driver.css";
import OnboardingFloatingMenu from "@/pages/brand/onboarding/OnboardingFloatingMenu";
import { ShopifyReviewModal } from "@/components/ShopifyReviewModal";
import { DevStoreUpgradeModal } from "@/components/DevStoreUpgradeModal";
import { FounderCallModal } from "@/components/FounderCallModal";
import { useShopifyReviewPrompt } from "@/hooks/useShopifyReviewPrompt";
import { useDevStoreUpgradePrompt } from "@/hooks/useDevStoreUpgradePrompt";
import { useFounderCallPrompt } from "@/hooks/useFounderCallPrompt";
import { DevStoreBanner } from "@/components/brand/DevStoreBanner";
import { useBrand } from "@/contexts/BrandContext/hooks";
import { AssistantLauncher, AssistantPanel } from "@/components/assistant";
import { useAssistantAccess } from "@/hooks/useAssistantAccess";

function BrandLayout() {
  const location = useLocation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { brandDetails } = useBrand();

  // Shopify Review Prompt
  const { isModalOpen, eligibility, handleReview, handleDismiss, handleClose } =
    useShopifyReviewPrompt();

  // Dev store upgrade prompt (login + every 24h)
  const {
    isModalOpen: isDevStoreModalOpen,
    handleClose: handleDevStoreClose,
  } = useDevStoreUpgradePrompt();

  // Founder onboarding call (first month, until booked; "Maybe later" snoozes)
  const {
    isModalOpen: isFounderCallModalOpen,
    handleBookCall,
    handleMaybeLater,
  } = useFounderCallPrompt();

  // Check if brand is using a dev store
  const isDevStore = brandDetails?.isDevStore === true;

  // Assistant panel (U1). Open state lives in sessionStorage because this
  // layout remounts on every navigation and the panel stays with the brand.
  const assistant = useAssistantAccess();
  const [isAssistantOpen, setIsAssistantOpen] = useState(
    () => sessionStorage.getItem("assistant.open") === "1"
  );
  const setAssistantOpen = (open: boolean) => {
    sessionStorage.setItem("assistant.open", open ? "1" : "0");
    setIsAssistantOpen(open);
  };

  // White-label: document title + favicon follow the tenant. When the brand
  // has no logo yet the index.html Reveshare favicon stays in place.
  const { name: brandName, logo: brandLogo } = useBrandIdentity();
  // Tab icon follows the brand's logo (see lib/favicon for why not Helmet).
  useFavicon(brandLogo ?? null);

  useEffect(() => {
    const handleResize = () => {
      // Close mobile sidebar when switching to desktop
      if (window.innerWidth >= 768 && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileSidebarOpen]);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };


  // Pages that own their whole canvas: no gutter, no max width. The program
  // page builder needs the room for its side-by-side preview.
  const isFullBleed = ["/brand/rewards", "/brand/program-page"].includes(location.pathname);

  return (
    <div className="flex relative">
      <Helmet>
        <title>{brandName ?? "Reveshare"}</title>
      </Helmet>
      <OnboardingFloatingMenu isVisible={true} />

      {/* Dev Store Banner - Top element at very top, scrolls with content */}
      {isDevStore && (
        <div className="absolute top-0 left-0 right-0 z-30">
          <DevStoreBanner />
        </div>
      )}

      {/* Mobile menu button */}
      <button
        onClick={toggleMobileSidebar}
        aria-label="Open menu"
        className="fixed top-4 left-4 z-50 p-2 bg-card rounded-lg shadow-lg border border-border hover:bg-accent transition-colors md:hidden"
      >
        <Menu className="w-5 h-5 text-muted-foreground" />
      </button>

      <Sidebar
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={closeMobileSidebar}
      />

      {/* min-w-0 + overflow-x-clip: the page itself never scrolls
          horizontally — wide content (tables) scrolls in its own container.
          clip (not hidden/auto) on purpose: the window is the vertical
          scroller here, and hidden/auto would make <main> a scroll container,
          which silently breaks position:sticky inside it (the dashboard's
          pinned section nav). The layout owns the page gutter; pages only
          contribute space-y-*. pt-16 on mobile clears the fixed menu button. */}
      <main
        className={`flex-1 min-w-0 overflow-x-clip ${
          isFullBleed ? "" : "px-4 sm:px-6 lg:px-8 pt-16 md:pt-8 pb-8"
        }`}
      >
        {isFullBleed ? (
          <Outlet />
        ) : (
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        )}
      </main>

      {assistant.enabled && (
        <>
          <AssistantLauncher open={isAssistantOpen} onOpen={() => setAssistantOpen(true)} />
          <AssistantPanel open={isAssistantOpen} onClose={() => setAssistantOpen(false)} />
        </>
      )}

      {/* Shopify Review Modal */}
      <ShopifyReviewModal
        isOpen={isModalOpen}
        onClose={handleClose}
        onReview={handleReview}
        onDismiss={handleDismiss}
        milestoneReached={eligibility?.milestoneReached}
      />

      {/* Dev Store Upgrade Modal */}
      <DevStoreUpgradeModal
        isOpen={isDevStoreModalOpen}
        onClose={handleDevStoreClose}
      />

      {/* Founder Call Modal */}
      <FounderCallModal
        isOpen={isFounderCallModalOpen}
        onBookCall={handleBookCall}
        onMaybeLater={handleMaybeLater}
      />
    </div>
  );
}

export default function BrandLayoutWithProviders() {
  return (
    <BrandProvider>
      <DriverProvider>
        <ModalProvider>
          <BrandLayout />
        </ModalProvider>
      </DriverProvider>
    </BrandProvider>
  );
}
